import { useCallback, useEffect, useMemo, useState } from "react";
import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPinnedColumnFields,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import ApiService from "../api/BaseApiService";
import { TemplateToolbar, ToolbarTemplate } from "./TemplateToolbar";
import { ColumnOrderOp, DataGridState, DataGridTemplate } from "./dataGridTypes";

type DefaultDataGridTemplate = {
  name: string;
  starred: boolean;
  state: Partial<DataGridState>;
};

const applyOrderOps = (baseFields: string[], ops: ColumnOrderOp[]): string[] => {
  let list = [...baseFields];

  const remove = (id: string) => {
    const i = list.indexOf(id);
    if (i >= 0) list.splice(i, 1);
  };

  for (const op of ops) {
    if (!baseFields.includes(op.id)) continue;

    remove(op.id);

    if (op.position === "start") {
      list.unshift(op.id);
      continue;
    }

    if (op.position === "end") {
      list.push(op.id);
      continue;
    }

    if (!("anchorId" in op)) continue;

    const anchorIndex = list.indexOf(op.anchorId);
    if (anchorIndex < 0) {
      list.push(op.id);
      continue;
    }

    const insertIndex = op.position === "before" ? anchorIndex : anchorIndex + 1;
    list.splice(insertIndex, 0, op.id);
  }

  const seen = new Set<string>();
  list = list.filter((id) => (seen.has(id) ? false : (seen.add(id), true)));

  return list;
};

const deriveOrderOpFromTargetIndex = (currentOrder: string[], id: string, targetIndex: number): ColumnOrderOp | null => {
  if (!currentOrder.includes(id)) return null;

  const list = currentOrder.filter((x) => x !== id);
  const clampedIndex = Math.max(0, Math.min(targetIndex, list.length));
  list.splice(clampedIndex, 0, id);

  const newIndex = list.indexOf(id);

  if (newIndex === 0) {
    return { type: "move", id, position: "start" };
  }

  if (newIndex === list.length - 1) {
    return { type: "move", id, position: "end" };
  }

  const anchorId = list[newIndex - 1];
  if (!anchorId || anchorId === id) return null;

  return { type: "move", id, position: "after", anchorId };
};

const reorderColumnsByFields = (columns: GridColDef[], orderedFields: string[]): GridColDef[] => {
  const byField = new Map(columns.map((c) => [c.field, c]));
  const inOrder: GridColDef[] = [];

  for (const field of orderedFields) {
    const column = byField.get(field);
    if (column) {
      inOrder.push(column);
      byField.delete(field);
    }
  }

  for (const column of columns) {
    if (byField.has(column.field)) {
      inOrder.push(column);
      byField.delete(column.field);
    }
  }

  return inOrder;
};

const compactOps = (ops: ColumnOrderOp[]): ColumnOrderOp[] => {
  const lastIndex = new Map<string, number>();
  ops.forEach((op, idx) => lastIndex.set(op.id, idx));
  return ops.filter((op, idx) => lastIndex.get(op.id) === idx);
};

const normalizeObject = <T extends Record<string, unknown>>(value: unknown, fallback: T): T => {
  if (Array.isArray(value) && value.length === 0) return {};
  if (value && typeof value === "object" && !Array.isArray(value)) return value as T;
  return fallback;
};

const normalizeDataGridState = (state: Partial<DataGridState> | undefined, fallback?: Partial<DataGridState>): DataGridState => ({
  columnWidths: normalizeObject(state?.columnWidths, normalizeObject(fallback?.columnWidths, {})),
  columnVisibilityModel: normalizeObject(state?.columnVisibilityModel, normalizeObject(fallback?.columnVisibilityModel, {})),
  filterModel: state?.filterModel ?? fallback?.filterModel ?? { items: [] },
  sortModel: state?.sortModel ?? fallback?.sortModel ?? [],
  pinnedColumns: state?.pinnedColumns ?? fallback?.pinnedColumns ?? { left: [], right: [] },
  orderOps: state?.orderOps ?? fallback?.orderOps ?? [],
});

const stableStringify = (value: unknown): string =>
  JSON.stringify(value, (_key, v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      return Object.keys(v)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (v as Record<string, unknown>)[key];
          return acc;
        }, {});
    }

    return v;
  });

const isStateEqual = (a: DataGridState, b: DataGridState) => {
  const a1 = stableStringify(a);
  const b1 = stableStringify(b);
  const equal = a1 === b1;

  console.log("[DataGridTemplates] isStateEqual", {
    equal,
    a,
    b,
    aString: a1,
    bString: b1,
  });

  return equal;
};

const normalizeTemplateName = (name: string) => name.trim().toLocaleLowerCase();

const createLocalTemplateId = (name: string): number => {
  let hash = 0;

  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }

  return -Math.abs(hash || 1);
};

interface DataGridStore {
  grids: Record<string, DataGridState>;
  activeTemplates: Record<string, number | null>;
  setGridState: (key: string, state: Partial<DataGridState>) => void;
  getGridState: (key: string, initialState?: Partial<DataGridState>) => DataGridState;
  setActiveTemplate: (key: string, templateId: number | null) => void;
}

export const useDataGridStorage = create<DataGridStore>()(
  devtools(
    persist(
      (set, get) => ({
        grids: {},
        activeTemplates: {},

        setGridState: (key, state) =>
          set((prev) => ({
            grids: {
              ...prev.grids,
              [key]: {
                ...(prev.grids[key] ?? {}),
                ...state,
              },
            },
          })),

        getGridState: (key, initialState = {}) => ({
          columnWidths: get().grids[key]?.columnWidths ?? {},
          sortModel: get().grids[key]?.sortModel ?? initialState.sortModel ?? [],
          filterModel: get().grids[key]?.filterModel ?? initialState.filterModel ?? { items: [] },
          columnVisibilityModel: get().grids[key]?.columnVisibilityModel ?? initialState.columnVisibilityModel ?? {},
          pinnedColumns: get().grids[key]?.pinnedColumns ?? initialState.pinnedColumns ?? { left: [], right: [] },
          orderOps: get().grids[key]?.orderOps ?? initialState.orderOps ?? [],
        }),

        setActiveTemplate: (key, templateId) =>
          set((prev) => ({
            activeTemplates: {
              ...prev.activeTemplates,
              [key]: templateId,
            },
          })),
      }),
      {
        name: "datagrid-storage",
      },
    ),
  ),
);

export const usePersistentDataGrid = (
  storageKey: string,
  columns: GridColDef[],
  initialState?: Partial<DataGridState>,
  defaultTemplates: DefaultDataGridTemplate[] = [],
) => {
  const { getGridState, setGridState, setActiveTemplate } = useDataGridStorage();

  const [serverTemplates, setServerTemplates] = useState<DataGridTemplate[]>([]);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [togglingStarTemplateId, setTogglingStarTemplateId] = useState<number | null>(null);

  const storedState = getGridState(storageKey, initialState);

  const defaultState = useMemo(() => normalizeDataGridState(initialState ?? {}), [initialState]);

  const normalizedStoredState = useMemo(() => normalizeDataGridState(storedState, initialState), [initialState, storedState]);

  const activeTemplateId = useDataGridStorage((state) => state.activeTemplates[storageKey] ?? null);

  const currentTemplates = useMemo<ToolbarTemplate[]>(() => {
    const serverNameSet = new Set(serverTemplates.map((template) => normalizeTemplateName(template.name)));

    const localTemplates: ToolbarTemplate[] = defaultTemplates
      .filter((template) => !serverNameSet.has(normalizeTemplateName(template.name)))
      .map((template) => ({
        id: createLocalTemplateId(template.name),
        name: template.name,
        starred: template.starred,
        state: normalizeDataGridState(template.state, initialState),
        isServerTemplate: false,
      }));

    const remoteTemplates: ToolbarTemplate[] = serverTemplates.map((template) => ({
      ...template,
      state: normalizeDataGridState(template.state, initialState),
      isServerTemplate: true,
    }));

    const merged = [...remoteTemplates, ...localTemplates].sort((a, b) => a.name.localeCompare(b.name));

    console.log("[DataGridTemplates] currentTemplates rebuilt", {
      storageKey,
      serverTemplates: remoteTemplates.map((template) => ({
        id: template.id,
        name: template.name,
        isServerTemplate: template.isServerTemplate,
        state: template.state,
      })),
      localTemplates: localTemplates.map((template) => ({
        id: template.id,
        name: template.name,
        isServerTemplate: template.isServerTemplate,
        state: template.state,
      })),
      mergedTemplates: merged.map((template) => ({
        id: template.id,
        name: template.name,
        isServerTemplate: template.isServerTemplate,
        state: template.state,
      })),
    });

    return merged;
  }, [defaultTemplates, initialState, serverTemplates, storageKey]);

  const isDefaultState = useMemo(() => isStateEqual(normalizedStoredState, defaultState), [defaultState, normalizedStoredState]);

  const fetchTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);

    try {
      const remoteTemplates = await ApiService.getEntitiesWithFilter("dataGridTemplate", { gridKey: storageKey, createdBy: "%USER" })();
      console.log("[DataGridTemplates] fetched server templates", {
        storageKey,
        remoteTemplates,
      });
      setServerTemplates(remoteTemplates ?? []);
    } catch (error) {
      console.error("Failed to load data grid templates", error);
      setServerTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [storageKey]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (activeTemplateId && !currentTemplates.some((template) => template.id === activeTemplateId)) {
      console.log("[DataGridTemplates] active template no longer exists, clearing it", {
        storageKey,
        activeTemplateId,
        currentTemplateIds: currentTemplates.map((template) => template.id),
      });
      setActiveTemplate(storageKey, null);
    }
  }, [activeTemplateId, currentTemplates, setActiveTemplate, storageKey]);

  useEffect(() => {
    const matchingTemplate = currentTemplates.find((template) => {
      const normalizedTemplateState = normalizeDataGridState(template.state, initialState);
      const equal = isStateEqual(normalizedStoredState, normalizedTemplateState);

      console.log("[DataGridTemplates] sync active template compare", {
        storageKey,
        activeTemplateId,
        templateId: template.id,
        templateName: template.name,
        isServerTemplate: template.isServerTemplate,
        normalizedStoredState,
        normalizedTemplateState,
        equal,
      });

      return equal;
    });

    const nextActiveTemplateId = matchingTemplate?.id ?? null;

    console.log("[DataGridTemplates] sync active template result", {
      storageKey,
      currentActiveTemplateId: activeTemplateId,
      nextActiveTemplateId,
      nextActiveTemplateName: matchingTemplate?.name ?? null,
    });

    if (activeTemplateId !== nextActiveTemplateId) {
      setActiveTemplate(storageKey, nextActiveTemplateId);
    }
  }, [activeTemplateId, currentTemplates, initialState, normalizedStoredState, setActiveTemplate, storageKey]);

  const handleSaveTemplate = useCallback(
    async (name?: string) => {
      const normalizedState = normalizedStoredState;
      const nameToUse = name?.trim() || `Ansicht ${currentTemplates.length + 1}`;
      const normalizedName = normalizeTemplateName(nameToUse);

      setIsSavingTemplate(true);

      try {
        const existingServerTemplate = serverTemplates.find((template) => normalizeTemplateName(template.name) === normalizedName);

        const saved = existingServerTemplate
          ? await ApiService.editEntity("dataGridTemplate")({
              id: existingServerTemplate.id,
              name: nameToUse,
              state: normalizedState,
            })
          : await ApiService.createEntity("dataGridTemplate")({
              gridKey: storageKey,
              name: nameToUse,
              state: normalizedState,
            });

        console.log("[DataGridTemplates] saved template", {
          storageKey,
          inputName: name,
          nameToUse,
          normalizedState,
          existingServerTemplate,
          saved,
        });

        setServerTemplates((prev) => {
          const next = prev.filter((template) => template.id !== saved.id);
          return [...next, saved];
        });

        setActiveTemplate(storageKey, saved.id);
      } catch (error) {
        console.error("Failed to save data grid template", error);
      } finally {
        setIsSavingTemplate(false);
      }
    },
    [currentTemplates.length, normalizedStoredState, serverTemplates, setActiveTemplate, storageKey],
  );

  const handleLoadTemplate = useCallback(
    (template: ToolbarTemplate) => {
      const normalizedState = normalizeDataGridState(template.state, initialState);

      console.log("[DataGridTemplates] handleLoadTemplate", {
        storageKey,
        templateId: template.id,
        templateName: template.name,
        isServerTemplate: template.isServerTemplate,
        normalizedState,
      });

      setGridState(storageKey, normalizedState);
      setActiveTemplate(storageKey, template.id);
    },
    [initialState, setActiveTemplate, setGridState, storageKey],
  );

  const handleDeleteTemplate = useCallback(
    async (template: ToolbarTemplate) => {
      if (!template.isServerTemplate) return;

      setIsDeletingTemplate(true);

      try {
        await ApiService.deleteEntity("dataGridTemplate", template.id)();

        console.log("[DataGridTemplates] deleted template", {
          storageKey,
          template,
        });

        setServerTemplates((prev) => prev.filter((item) => item.id !== template.id));

        if (activeTemplateId === template.id) {
          setActiveTemplate(storageKey, null);
        }
      } catch (error) {
        console.error("Failed to delete data grid template", error);
      } finally {
        setIsDeletingTemplate(false);
      }
    },
    [activeTemplateId, setActiveTemplate, storageKey],
  );

  const handleToggleTemplateStar = useCallback(
    async (template: ToolbarTemplate) => {
      setTogglingStarTemplateId(template.id);

      try {
        if (template.isServerTemplate) {
          await ApiService.editEntity("dataGridTemplate")({
            id: template.id,
            starred: !template.starred,
          });

          console.log("[DataGridTemplates] toggled server template star", {
            storageKey,
            templateId: template.id,
            templateName: template.name,
            oldStarred: template.starred,
            newStarred: !template.starred,
          });

          setServerTemplates((prev) =>
            prev.map((item) =>
              item.id === template.id
                ? {
                    ...item,
                    starred: !item.starred,
                  }
                : item,
            ),
          );
        } else {
          const created = await ApiService.createEntity("dataGridTemplate")({
            gridKey: storageKey,
            name: template.name,
            starred: !template.starred,
            state: normalizeDataGridState(template.state, initialState),
          });

          console.log("[DataGridTemplates] promoted local template to server template via star toggle", {
            storageKey,
            oldTemplate: template,
            created,
          });

          setServerTemplates((prev) => {
            const withoutSameName = prev.filter((item) => normalizeTemplateName(item.name) !== normalizeTemplateName(created.name));
            return [...withoutSameName, created];
          });

          if (activeTemplateId === template.id) {
            setActiveTemplate(storageKey, created.id);
          }
        }
      } catch (error) {
        console.error("Failed to toggle starred state for data grid template", error);
      } finally {
        setTogglingStarTemplateId(null);
      }
    },
    [activeTemplateId, initialState, setActiveTemplate, storageKey],
  );

  const handleResetToDefault = useCallback(() => {
    console.log("[DataGridTemplates] handleResetToDefault", {
      storageKey,
      defaultState,
    });

    setGridState(storageKey, defaultState);
    setActiveTemplate(storageKey, null);
  }, [defaultState, setActiveTemplate, setGridState, storageKey]);

  const updateGridState = useCallback(
    (partialState: Partial<DataGridState>) => {
      const nextState = normalizeDataGridState({ ...normalizedStoredState, ...partialState }, initialState);

      console.log("[DataGridTemplates] updateGridState called", {
        storageKey,
        partialState,
        normalizedStoredState,
        nextState,
        activeTemplateIdBefore: useDataGridStorage.getState().activeTemplates[storageKey] ?? null,
        currentTemplates: currentTemplates.map((template) => ({
          id: template.id,
          name: template.name,
          isServerTemplate: template.isServerTemplate,
          rawState: template.state,
          normalizedState: normalizeDataGridState(template.state, initialState),
        })),
      });

      const matchingTemplate = currentTemplates.find((template) => {
        const normalizedTemplateState = normalizeDataGridState(template.state, initialState);
        const equal = isStateEqual(nextState, normalizedTemplateState);

        console.log("[DataGridTemplates] comparing next state to template", {
          storageKey,
          templateId: template.id,
          templateName: template.name,
          isServerTemplate: template.isServerTemplate,
          nextState,
          normalizedTemplateState,
          equal,
        });

        return equal;
      });

      console.log("[DataGridTemplates] updateGridState result", {
        storageKey,
        matchingTemplateId: matchingTemplate?.id ?? null,
        matchingTemplateName: matchingTemplate?.name ?? null,
      });

      setGridState(storageKey, partialState);
      setActiveTemplate(storageKey, matchingTemplate?.id ?? null);
    },
    [currentTemplates, initialState, normalizedStoredState, setActiveTemplate, setGridState, storageKey],
  );

  const baseFields = useMemo(() => columns.map((c) => c.field), [columns]);

  const orderedFields = useMemo(() => applyOrderOps(baseFields, normalizedStoredState.orderOps), [baseFields, normalizedStoredState.orderOps]);

  const orderedColumns = useMemo(() => reorderColumnsByFields(columns, orderedFields), [columns, orderedFields]);

  const columnsWithWidths = useMemo(
    () =>
      orderedColumns.map((column) => {
        if (column.field in normalizedStoredState.columnWidths) {
          const { flex, ...rest } = column;
          return {
            ...rest,
            width: normalizedStoredState.columnWidths[column.field],
          };
        }

        return column;
      }),
    [normalizedStoredState.columnWidths, orderedColumns],
  );

  const getLatestOps = useCallback(() => useDataGridStorage.getState().grids[storageKey]?.orderOps ?? [], [storageKey]);

  const pinnedColumns = useMemo(
    () => ({
      left: normalizedStoredState.pinnedColumns.left ?? [],
      right: normalizedStoredState.pinnedColumns.right ?? [],
    }),
    [normalizedStoredState.pinnedColumns],
  );

  const gridProps = {
    sortModel: normalizedStoredState.sortModel,
    onSortModelChange: (model: GridSortModel) => updateGridState({ sortModel: model }),

    filterModel: normalizedStoredState.filterModel,
    onFilterModelChange: (model: GridFilterModel) => updateGridState({ filterModel: model }),

    columnVisibilityModel: normalizedStoredState.columnVisibilityModel,
    onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => updateGridState({ columnVisibilityModel: model }),

    pinnedColumns,
    onPinnedColumnsChange: (model: GridPinnedColumnFields) => updateGridState({ pinnedColumns: model }),

    columns: columnsWithWidths,
    onColumnWidthChange: (params: GridColumnResizeParams) => {
      const columnId = params.colDef.field;
      const width = params.width;

      updateGridState({
        columnWidths: {
          ...normalizedStoredState.columnWidths,
          [columnId]: width,
        },
      });
    },

    onColumnOrderChange: (params: GridColumnOrderChangeParams) => {
      const id = params.column.field;
      const targetIndex = params.targetIndex;

      const latestOps = getLatestOps();
      const currentOrder = applyOrderOps(baseFields, latestOps);
      const op = deriveOrderOpFromTargetIndex(currentOrder, id, targetIndex);

      console.log("[DataGridTemplates] onColumnOrderChange", {
        storageKey,
        id,
        targetIndex,
        latestOps,
        currentOrder,
        derivedOp: op,
      });

      if (!op) return;

      const nextOps = compactOps([...latestOps, op]);
      updateGridState({ orderOps: nextOps });
    },

    slots: {
      toolbar: TemplateToolbar,
    },

    slotProps: {
      toolbar: {
        templates: currentTemplates,
        activeTemplateId,
        showReset: !isDefaultState,
        isLoadingTemplates,
        isSavingTemplate,
        isDeletingTemplate,
        togglingStarTemplateId,
        onSaveTemplate: handleSaveTemplate,
        onLoadTemplate: handleLoadTemplate,
        onDeleteTemplate: handleDeleteTemplate,
        onToggleTemplateStar: handleToggleTemplateStar,
        onReset: handleResetToDefault,
        disableSaving: false,
        csvOptions: {
          delimiter: ";",
          utf8WithBom: true,
        },
        quickFilterProps: {
          debounceMs: 300,
        },
      },
    },
  };

  return { gridProps };
};
